import React, { useState, useEffect } from 'react';
import { 
  BarChart3, TrendingUp, Filter, 
  Calendar, Download, Printer,
  ArrowLeft
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

const MRPForecast = ({ setActiveView }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [forecasts, setForecasts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Helper functions - defined before they are used
  const getProductDetails = (productId) => {
    return products.find(p => p.product_id === productId) || { name: productId || '未知產品', product_code: productId || 'N/A' };
  };
  
  const getProductCategory = (productId) => {
    const product = products.find(p => p.product_id === productId);
    return product ? product.category : '未知類別';
  };
  
  const getTrend = (confidenceLevel) => {
    if (confidenceLevel >= 80) return 'up';
    if (confidenceLevel <= 60) return 'down';
    return 'stable';
  };
  
  // Fetch forecast data from Supabase
  useEffect(() => {
    const fetchForecastData = async () => {
      try {
        setLoading(true);
        
        // Fetch sales projections
        const { data: forecastsData, error: forecastsError } = await supabase
          .from('sales_projections')
          .select('*')
          .order('projection_date', { ascending: false });

        if (forecastsError) {
          console.error('Error fetching sales projections:', forecastsError);
          return;
        }

        // Fetch products
        const { data: productsData, error: productsError } = await supabase
          .from('products')
          .select('*');

        if (productsError) {
          console.error('Error fetching products:', productsError);
          return;
        }

        setForecasts(forecastsData || []);
        setProducts(productsData || []);
      } catch (error) {
        console.error('Error fetching forecast data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchForecastData();
  }, []);
  
  // Filter forecasts based on selections
  const filteredForecasts = forecasts.filter(forecast => {
    return (selectedPeriod === 'all' || forecast.forecast_period === selectedPeriod) &&
           (selectedCategory === 'all' || getProductCategory(forecast.product_id) === selectedCategory);
  });
  
  // Extract unique periods and categories for filters
  const periods = ['all', ...new Set(forecasts.map(f => f.forecast_period))];
  const categories = ['all', ...new Set(forecasts.map(f => getProductCategory(f.product_id)))];
  
  // Calculate forecast summary
  const forecastSummary = {
    totalDemand: filteredForecasts.reduce((sum, f) => sum + (f.projected_quantity || 0), 0),
    productCount: new Set(filteredForecasts.map(f => f.product_id)).size,
    forecastPeriod: selectedPeriod === 'all' ? '所有期間' : selectedPeriod
  };
  
  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button 
            onClick={() => setActiveView('dashboard')} 
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <h2 className="text-2xl font-bold text-gray-800">需求預測分析</h2>
        </div>
        <div className="flex space-x-2">
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center">
            <Download className="w-4 h-4 mr-2" />
            匯出報表
          </button>
          <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors flex items-center">
            <Printer className="w-4 h-4 mr-2" />
            列印
          </button>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">預測期間</label>
            <select 
              value={selectedPeriod} 
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {periods.map(period => (
                <option key={period} value={period}>
                  {period === 'all' ? '所有期間' : period}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">產品類別</label>
            <select 
              value={selectedCategory} 
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {categories.map(category => (
                <option key={category} value={category}>
                  {category === 'all' ? '所有類別' : category}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* Forecast Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">總預測需求量</p>
              <p className="text-2xl font-bold text-gray-800">{forecastSummary.totalDemand.toLocaleString()}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <BarChart3 className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">預測產品數量</p>
              <p className="text-2xl font-bold text-gray-800">{forecastSummary.productCount}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <Filter className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">預測期間</p>
              <p className="text-2xl font-bold text-gray-800">{forecastSummary.forecastPeriod}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <Calendar className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Forecast Details */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">需求預測明細</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">產品</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">類別</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">期間</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">預測數量</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">信心水平</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">趨勢</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">加載中...</td>
                </tr>
              ) : filteredForecasts.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">沒有找到相關的預測資料。</td>
                </tr>
              ) : filteredForecasts.map((forecast, index) => {
                const product = getProductDetails(forecast.product_id);
                const trend = getTrend(forecast.confidence_level);
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{product.name}</div>
                          <div className="text-sm text-gray-500">{product.product_code}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {getProductCategory(forecast.product_id)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {forecast.forecast_period}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {forecast.projected_quantity?.toLocaleString()} 個
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full" 
                            style={{width: `${forecast.confidence_level}%`}}
                          ></div>
                        </div>
                        <span className="ml-2 text-sm text-gray-500">{forecast.confidence_level}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {trend === 'up' ? (
                        <div className="flex items-center text-green-600">
                          <TrendingUp className="w-4 h-4 mr-1" />
                          上升
                        </div>
                      ) : trend === 'down' ? (
                        <div className="flex items-center text-red-600">
                          <TrendingUp className="w-4 h-4 mr-1 transform rotate-180" />
                          下降
                        </div>
                      ) : (
                        <div className="flex items-center text-gray-600">
                          <span className="w-4 h-0.5 bg-gray-400 mr-1"></span>
                          持平
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MRPForecast;
